import {
  RectangleHorizontal,
  Flag,
  Shirt,
  Dumbbell,
  Megaphone,
  Newspaper,
  Share2,
  Globe,
  Camera,
  Gift,
  Link2,
  Handshake,
  Package,
  Croissant,
  Car,
  Wrench,
  Wallet,
  CalendarDays,
  Eye,
  ShoppingBag,
  Users,
  HeartHandshake,
} from "lucide-react";

/**
 * Beispieldaten und Rechenlogik der Demo. Alles ist erfunden, aber bewusst
 * realistisch für einen Kreisligisten gewählt: rund 150 Zuschauer je
 * Heimspiel, ein Instagram-Konto mit gut 1.500 Followern. Die Demo soll
 * zeigen, wie die Plattform arbeitet, nicht mit großen Zahlen beeindrucken.
 */

export const CLUB = {
  name: "TSV Musterstadt",
  meta: "Fußball · Kreisliga A · 1.240 Mitglieder",
  team: "1. Herren",
  squad: 22,
};

export const SEASON = {
  label: "Saison 2026/27",
  range: "01.08.2026 bis 31.05.2027",
  homeGames: 15,
};

/** Kalenderwoche des ersten Heimspiels, Beginn der Wochenreihen. */
const FIRST_WEEK = 33;

/** Bis zu diesem Heimspiel lässt sich in der Demo eintragen (Winterpause). */
export const ENTRY_LIMIT = 9;

/** Ab dieser Größe werden Ergebnisse kleiner Gruppen angezeigt. */
export const MIN_GROUP = 5;

const AWAY_SPECTATORS = 110;
const YOUTH_PER_WEEKEND = 140;
const TRAINING_VISITS_PER_WEEK = 60;
const SHARE_WEEKS = [34, 40, 46];

export const HOME_GAMES = [
  { n: 1, weekday: "So.", date: "16.08.", time: "15:00", opponent: "SV Nordheim", week: 33, spectators: 135, method: "laut Kasse" },
  { n: 2, weekday: "So.", date: "30.08.", time: "15:00", opponent: "FC Westfeld", week: 35, spectators: 160, method: "laut Kasse" },
  { n: 3, weekday: "So.", date: "13.09.", time: "15:00", opponent: "SpVgg Lindenau", week: 37, spectators: 120, method: "geschätzt" },
  { n: 4, weekday: "So.", date: "27.09.", time: "15:00", opponent: "TuS Ostbach", week: 39, spectators: 240, method: "laut Kasse", context: "Derby" },
  { n: 5, weekday: "So.", date: "11.10.", time: "15:00", opponent: "SC Brückenfeld", week: 41, spectators: 85, method: "geschätzt", context: "Regen" },
  { n: 6, weekday: "So.", date: "25.10.", time: "15:00", opponent: "DJK Hagenau", week: 43, spectators: 150, method: "gezählt" },
  { n: 7, weekday: "So.", date: "08.11.", time: "14:30", opponent: "VfL Seetal", week: 45, spectators: 140, method: "laut Kasse" },
  { n: 8, weekday: "So.", date: "22.11.", time: "14:30", opponent: "SG Oberau", week: 47 },
  { n: 9, weekday: "So.", date: "06.12.", time: "14:30", opponent: "1. FC Talheim", week: 49 },
  { n: 10, weekday: "So.", date: "07.03.", time: "15:00", opponent: "Eintracht Wiesental", week: 10 },
  { n: 11, weekday: "So.", date: "21.03.", time: "15:00", opponent: "BV Mühlenbach", week: 12 },
  { n: 12, weekday: "So.", date: "11.04.", time: "15:00", opponent: "SuS Fichtenau", week: 15 },
  { n: 13, weekday: "So.", date: "25.04.", time: "15:00", opponent: "TSG Birkenhain", week: 17 },
  { n: 14, weekday: "So.", date: "09.05.", time: "15:00", opponent: "Germania Steinfeld", week: 19 },
  { n: 15, weekday: "So.", date: "23.05.", time: "15:00", opponent: "SC Rheinau", week: 21 },
];

export const SPECTATOR_BUCKETS = [
  { id: "u50", label: "unter 50", value: 40 },
  { id: "50", label: "50 bis 100", value: 75 },
  { id: "100", label: "100 bis 200", value: 150 },
  { id: "200", label: "200 bis 400", value: 300 },
  { id: "400", label: "über 400", value: 450 },
];

export const METHODS = ["geschätzt", "gezählt", "laut Kasse"];

export const TARGETS = {
  website: "Website des Unternehmens",
  angebot: "Angebot oder Gutschein",
  karriere: "Karriereseite",
};

export const TARGET_OPTIONS = [
  { value: "website", label: "Website" },
  { value: "angebot", label: "Angebot" },
  { value: "karriere", label: "Karriere" },
];

export const FOCUS_OPTIONS = [
  {
    id: "sichtbarkeit",
    icon: Eye,
    label: "Sichtbarkeit",
    text: "Möglichst viele Menschen in der Region sollen euch wahrnehmen.",
  },
  {
    id: "kunden",
    icon: ShoppingBag,
    label: "Kunden",
    text: "Das Sponsoring soll Kundschaft in Laden, Praxis oder Autohaus bringen.",
  },
  {
    id: "mitarbeitende",
    icon: Users,
    label: "Mitarbeitende",
    text: "Ihr sucht Azubis und Fachkräfte aus der Region.",
  },
  {
    id: "engagement",
    icon: HeartHandshake,
    label: "Engagement",
    text: "Ihr wollt zeigen, was ihr im Ort möglich macht.",
  },
];

export const FOCUS_BY_ID = Object.fromEntries(FOCUS_OPTIONS.map((f) => [f.id, f]));

export const BLOCK_GROUPS = [
  { id: "praesenz", label: "Präsenz", hint: "dauerhaft sichtbar" },
  { id: "spieltag", label: "Spieltag", hint: "bei jedem Heimspiel" },
  { id: "digital", label: "Digital", hint: "Kanäle und Inhalte" },
  { id: "angebote", label: "Angebote", hint: "für Spieler und Gruppen" },
  { id: "begegnung", label: "Begegnung und Sonstiges", hint: "" },
];

/**
 * Die Bausteine eines Sponsorings. `qty` macht die Menge einstellbar, `qr`
 * erlaubt einen eigenen QR-Code, `target` ein Ziel für den Link.
 */
export const BLOCKS = [
  {
    id: "bande",
    group: "praesenz",
    icon: RectangleHorizontal,
    title: "Bandenwerbung",
    short: "Am Hauptplatz, belegt per Foto",
    qty: { label: "Banden", min: 1, max: 3, step: 1 },
    qr: true,
  },
  {
    id: "banner",
    group: "praesenz",
    icon: Flag,
    title: "Banner",
    short: "Im Vereinsheim oder am Zaun",
    qty: { label: "Banner", min: 1, max: 3, step: 1 },
    qr: true,
  },
  {
    id: "trikot",
    group: "praesenz",
    icon: Shirt,
    title: "Trikotsponsoring",
    short: "Logo auf dem Spieltrikot der 1. Herren",
  },
  {
    id: "training",
    group: "praesenz",
    icon: Dumbbell,
    title: "Trainingskleidung und -utensilien",
    short: "Ausstattung mit eurem Logo",
    qty: { label: "Mannschaften", min: 1, max: 4, step: 1 },
  },
  {
    id: "durchsage",
    group: "spieltag",
    icon: Megaphone,
    title: "Stadiondurchsage",
    short: "Bei jedem Heimspiel, abgehakt am Spieltag",
  },
  {
    id: "heft",
    group: "spieltag",
    icon: Newspaper,
    title: "Anzeige in Heft oder Flyer",
    short: "Je Heimspiel, Auflage 200",
    qr: true,
  },
  {
    id: "social",
    group: "digital",
    icon: Share2,
    title: "Social-Media-Beiträge",
    short: "Automatisch erkannt über die Schnittstelle",
    qty: { label: "Beiträge", min: 4, max: 12, step: 2 },
  },
  {
    id: "website",
    group: "digital",
    icon: Globe,
    title: "Nennung auf der Vereinswebsite",
    short: "Mit eigenem Tracking-Link, regelmäßig geprüft",
  },
  {
    id: "fotos",
    group: "digital",
    icon: Camera,
    title: "Fotos für eure Kanäle",
    short: "Der Verein lädt hoch, ihr ladet herunter",
    qty: { label: "Fotos", min: 6, max: 24, step: 6 },
  },
  {
    id: "vorteil",
    group: "angebote",
    icon: Gift,
    title: "Vorteil für Spieler",
    short: "Ein Angebot nur für die 22 Spieler der 1. Herren",
  },
  {
    id: "gruppenlink",
    group: "angebote",
    icon: Link2,
    title: "Link in Vereinsgruppen",
    short: "Zum Beispiel ein Bewerbungslink in den WhatsApp-Gruppen",
    target: true,
  },
  {
    id: "treff",
    group: "begegnung",
    icon: Handshake,
    title: "Sponsorentreff",
    short: "Treffen der Partner im Vereinsheim",
    qty: { label: "Termine", min: 1, max: 3, step: 1 },
  },
  {
    id: "sonstiges",
    group: "begegnung",
    icon: Package,
    title: "Sonstige Leistung",
    short: "Frei benennbar, für alles Besondere im Vertrag",
    freeText: true,
  },
];

export const BLOCK_BY_ID = Object.fromEntries(BLOCKS.map((b) => [b.id, b]));

const BLOCK_DEFAULTS = {
  bande: { qty: 1, qr: false },
  banner: { qty: 1, qr: false },
  training: { qty: 2 },
  heft: { qr: true },
  social: { qty: 8 },
  fotos: { qty: 12 },
  gruppenlink: { target: "karriere" },
  treff: { qty: 2 },
};

export const SPONSORS = [
  {
    id: "baeckerei",
    name: "Bäckerei Schmitz",
    branch: "Bäckerei mit drei Filialen im Ort",
    icon: Croissant,
    inKindLabel: "Gutschein für den Mannschaftsabend",
    offerLabel: "Rabatt für Spieler",
    otherLabel: "Name auf dem Jugendturnier",
    preset: {
      volume: 2400,
      inKind: true,
      focus: "kunden",
      spectators: "alle",
      qrTarget: "angebot",
      shareResults: true,
      blocks: {
        bande: { on: true, qty: 1, qr: true },
        banner: { on: true, qty: 1, qr: true },
        durchsage: { on: true },
        heft: { on: true, qr: true },
        social: { on: true, qty: 8 },
        website: { on: true },
        treff: { on: true, qty: 1 },
      },
    },
  },
  {
    id: "autohaus",
    name: "Autohaus Meyer",
    branch: "Autohaus mit Werkstatt",
    icon: Car,
    inKindLabel: "Kleinbus für Auswärtsfahrten",
    offerLabel: "Leasing-Vorteil für Spieler",
    otherLabel: "Logo auf dem Vereinsbus",
    preset: {
      volume: 6000,
      inKind: true,
      focus: "sichtbarkeit",
      spectators: "alle",
      qrTarget: "website",
      shareResults: true,
      blocks: {
        trikot: { on: true },
        bande: { on: true, qty: 2, qr: false },
        durchsage: { on: true },
        social: { on: true, qty: 12 },
        website: { on: true },
        fotos: { on: true, qty: 12 },
        vorteil: { on: true },
        treff: { on: true, qty: 2 },
        sonstiges: { on: true },
      },
    },
  },
  {
    id: "elektro",
    name: "Elektro Brandt",
    branch: "Handwerksbetrieb mit 25 Mitarbeitenden",
    icon: Wrench,
    inKindLabel: "Wartung der Flutlichtanlage",
    offerLabel: "Rabatt für Spieler",
    otherLabel: "Name auf der Flutlichtanlage",
    preset: {
      volume: 1800,
      inKind: false,
      focus: "mitarbeitende",
      spectators: "erste",
      qrTarget: "karriere",
      shareResults: false,
      blocks: {
        bande: { on: true, qty: 1, qr: true },
        training: { on: true, qty: 2 },
        social: { on: true, qty: 4 },
        website: { on: true },
        gruppenlink: { on: true, target: "karriere" },
        treff: { on: true, qty: 1 },
      },
    },
  },
];

export const SPONSOR_BY_ID = Object.fromEntries(SPONSORS.map((s) => [s.id, s]));

/** Erzeugt ein vollständiges Setup aus dem Paket eines Beispiel-Unternehmens. */
export function createSetup(sponsorId) {
  const { blocks, ...rest } = SPONSOR_BY_ID[sponsorId].preset;
  const allBlocks = {};
  BLOCKS.forEach((block) => {
    allBlocks[block.id] = {
      ...(BLOCK_DEFAULTS[block.id] || {}),
      on: false,
      ...(blocks[block.id] || {}),
    };
  });
  return { sponsorId, ...rest, blocks: allBlocks };
}

/** Wie viele QR-Codes und Kurzlinks das Setup erzeugt. */
export function countTools(setup) {
  const b = setup.blocks;
  let qr = 0;
  ["bande", "banner"].forEach((id) => {
    if (b[id].on && b[id].qr) qr += b[id].qty;
  });
  if (b.heft.on && b.heft.qr) qr += 1;
  const links = ["website", "social", "gruppenlink", "vorteil"].filter(
    (id) => b[id].on
  ).length;
  return { qr, links };
}

// Leichte, feste Schwankung, damit die Wochenreihen nicht künstlich glatt
// aussehen, aber bei jedem Aufruf gleich bleiben.
const NOISE = [1, 0.85, 1.1, 0.95, 1.15, 0.8, 1.05, 0.9, 1.2, 0.85, 1, 1.1, 0.95, 1.05, 0.9, 1, 1.1];
const noise = (i) => NOISE[((i % NOISE.length) + NOISE.length) % NOISE.length];

const POSTS = [
  { type: "Beitrag", title: "Saisonstart: Danke an unsere Partner", date: "18.08.", views: 2140, interactions: 168 },
  { type: "Story", title: "Spieltagsplakat gegen FC Westfeld", date: "29.08.", views: 980, interactions: 41 },
  { type: "Reel", title: "Torjubel im Derby gegen Ostbach", date: "28.09.", views: 3420, interactions: 287 },
  { type: "Beitrag", title: "Partner im Porträt: {s}", date: "09.10.", views: 1610, interactions: 112 },
  { type: "Story", title: "Spieltagsplakat gegen DJK Hagenau", date: "24.10.", views: 1040, interactions: 38 },
  { type: "Beitrag", title: "Trainingsauftakt der A-Jugend", date: "04.11.", views: 1730, interactions: 126 },
  { type: "Reel", title: "Flutlichtspiel am Freitag", date: "20.11.", views: 2650, interactions: 204 },
  { type: "Beitrag", title: "Jahresrückblick der Jugend", date: "04.12.", views: 1880, interactions: 143 },
];

const PHOTO_CAPTIONS = [
  "Saisonauftakt", "Derby gegen Ostbach", "Trikotübergabe", "Jugendturnier",
  "Mannschaftsfoto", "Flutlichtspiel", "Training", "Vereinsfest",
  "Siegerehrung", "Auswärtsfahrt", "Kabine", "Fans",
  "Bambini", "Torjubel", "Halbzeit", "Ehrenamt",
  "Weihnachtsfeier", "Hallenturnier", "Rückrunde", "Sponsorentreff",
  "Abschlussfahrt", "Saisonfinale", "Aufstiegsfeier", "Danke",
];

const FOCUS_ORDER = {
  sichtbarkeit: ["kontakte", "social", "zuschauer", "klicks", "qr", "erfuellung"],
  kunden: ["ergebnis", "qr", "klicks", "social", "kontakte", "erfuellung"],
  mitarbeitende: ["ergebnis", "klicks", "social", "kontakte", "erfuellung"],
  engagement: ["erfuellung", "fotos", "zuschauer", "social", "kontakte"],
};

const nf = new Intl.NumberFormat("de-DE");
export const fmt = (n) => nf.format(Math.round(n));
export const euro = (n) => `${fmt(n)} €`;

const sum = (list) => list.reduce((a, b) => a + b, 0);
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

/**
 * Baut aus Setup, den Einträgen der Erfassung und den Meldungen des
 * Unternehmens alle Werte des Dashboards.
 */
export function buildDashboard(setup, entries = [], reports = {}, perspective = "unternehmen") {
  const sponsor = SPONSOR_BY_ID[setup.sponsorId];
  const b = setup.blocks;
  const on = (id) => Boolean(b[id]?.on);

  // Heimspiele: Ausgangsstand plus das, was in der Demo eingetragen wurde
  const games = HOME_GAMES.map((g) => {
    if (g.spectators != null) {
      return { ...g, status: "gespielt", done: { durchsage: true, heft: true } };
    }
    const entry = entries.find((e) => e.n === g.n);
    return entry ? { ...g, ...entry, entered: true } : { ...g, status: "geplant" };
  });
  const played = games.filter((g) => g.status === "gespielt");
  const passed = games.filter((g) => g.status !== "geplant");
  const nextGame = games.find((g) => g.status === "geplant" && g.n <= ENTRY_LIMIT) || null;
  const progress = passed.length / SEASON.homeGames;

  const lastWeek = Math.max(...passed.map((g) => g.week));
  const weeks = [];
  for (let w = FIRST_WEEK; w <= lastWeek; w += 1) weeks.push(w);

  // Zuschauer
  const tracked = setup.spectators !== "aus";
  const firstTeam = sum(played.map((g) => g.spectators ?? 0));
  const youth =
    setup.spectators === "alle"
      ? sum(weeks.map((w, i) => Math.round(YOUTH_PER_WEEKEND * noise(i + 5))))
      : 0;
  const methods = {};
  played.forEach((g) => {
    if (g.method && g.spectators != null) methods[g.method] = (methods[g.method] || 0) + 1;
  });
  const spectators = tracked
    ? {
        firstTeam,
        youth,
        total: firstTeam + youth,
        avg: played.length ? Math.round(firstTeam / played.length) : 0,
        methods,
        weekends: weeks.length,
      }
    : null;

  // Kontakte vor Ort, offen hochgerechnet
  const visibleOnSite = ["bande", "banner", "trikot", "durchsage", "heft"].some(on);
  let contacts = null;
  if (tracked && visibleOnSite) {
    const parts = [{ label: `Zuschauer ${CLUB.team}`, value: firstTeam, note: "Angabe Verein" }];
    if (youth) parts.push({ label: "Weitere Mannschaften", value: youth, note: "Angabe Verein" });
    if (on("bande") || on("banner")) {
      parts.push({
        label: "Training und Vereinsheim",
        value: weeks.length * TRAINING_VISITS_PER_WEEK,
        note: `geschätzt ${TRAINING_VISITS_PER_WEEK} Besuche je Woche`,
      });
    }
    if (on("trikot")) {
      parts.push({
        label: "Auswärtsspiele mit Trikot",
        value: played.length * AWAY_SPECTATORS,
        note: `geschätzt ${AWAY_SPECTATORS} Zuschauer je Spiel`,
      });
    }
    contacts = { total: Math.round(sum(parts.map((p) => p.value)) / 50) * 50, parts };
  }

  // QR-Codes: ein Code je Platzierung
  const qrPlacements = [
    on("heft") && b.heft.qr && { id: "heft", label: "Heft und Flyer", home: 7, off: 1, q: 1, shift: 0 },
    on("bande") && b.bande.qr && {
      id: "bande",
      label: b.bande.qty > 1 ? `Banden (${b.bande.qty})` : "Bande",
      home: 4,
      off: 1,
      q: b.bande.qty,
      shift: 3,
    },
    on("banner") && b.banner.qr && {
      id: "banner",
      label: b.banner.qty > 1 ? `Banner (${b.banner.qty})` : "Banner",
      home: 2.5,
      off: 1,
      q: b.banner.qty,
      shift: 7,
    },
  ].filter(Boolean);

  let qr = null;
  if (qrPlacements.length) {
    const totals = Object.fromEntries(qrPlacements.map((p) => [p.id, 0]));
    const series = weeks.map((w, i) => {
      const game = played.find((g) => g.week === w);
      const factor = game ? clamp((game.spectators ?? 140) / 140, 0.4, 2) : 0;
      let value = 0;
      qrPlacements.forEach((p) => {
        const v = Math.round((game ? p.home * factor : p.off) * p.q * noise(i + p.shift));
        totals[p.id] += v;
        value += v;
      });
      return {
        key: `w${w}`,
        label: String(w),
        value,
        highlight: Boolean(game),
        entered: Boolean(game?.entered),
        title: `KW ${w}: ${value} Scans${game ? `, Heimspiel gegen ${game.opponent}` : ""}`,
      };
    });
    qr = {
      total: sum(series.map((s) => s.value)),
      series,
      placements: qrPlacements.map((p) => ({ id: p.id, label: p.label, value: totals[p.id] })),
      target: setup.qrTarget,
    };
  }

  // Social-Media-Beiträge, über die Schnittstelle erkannt
  let social = null;
  if (on("social")) {
    const qty = b.social.qty;
    const due = Math.floor(qty * progress);
    const posts = POSTS.slice(0, Math.min(due, POSTS.length)).map((p) => ({
      ...p,
      title: p.title.replace("{s}", sponsor.name),
    }));
    const views = sum(posts.map((p) => p.views));
    const interactions = sum(posts.map((p) => p.interactions));
    social = {
      qty,
      due,
      done: posts.length,
      posts: [...posts].reverse(),
      views,
      interactions,
      rate: views ? (interactions / views) * 100 : 0,
    };
  }

  // Link-Klicks je Kanal
  const channels = [];
  if (on("website")) {
    channels.push({
      id: "website",
      label: "Vereinswebsite",
      value: sum(weeks.map((w, i) => Math.round(3 * noise(i + 2)))),
      hint: "Nennung mit eigenem Tracking-Link",
    });
  }
  if (social && social.done) {
    channels.push({
      id: "social",
      label: "Links in Beiträgen und Bio",
      value: social.done * 9,
      hint: "Link-Sticker und Link in der Bio",
    });
  }
  let groupClicks = 0;
  const shares = SHARE_WEEKS.filter((w) => w <= lastWeek).length;
  if (on("gruppenlink")) {
    groupClicks = sum(
      weeks.map((w) => (SHARE_WEEKS.includes(w) ? 14 : SHARE_WEEKS.includes(w - 1) ? 4 : 1))
    );
    channels.push({
      id: "gruppe",
      label: "Vereinsgruppen",
      value: groupClicks,
      hint: `${TARGETS[b.gruppenlink.target]}, ${shares} Mal in WhatsApp-Gruppen geteilt`,
    });
  }
  if (on("vorteil")) {
    channels.push({
      id: "vorteil",
      label: "Spielervorteil",
      value: 17,
      hint: `nur in der Mannschaftsgruppe der ${CLUB.team}`,
    });
  }
  const clicks = sum(channels.map((c) => c.value));

  // Ergebnisse, die das Unternehmen selbst meldet
  const results = [];
  const addResult = (key, label, detail, base, extra = {}) =>
    results.push({ key, label, detail, value: base + (reports[key] || 0), ...extra });

  const offerSources = [];
  let offerBase = 0;
  if (qr && setup.qrTarget === "angebot") {
    offerSources.push("QR-Codes");
    offerBase += Math.round(qr.total * 0.2);
  }
  if (on("gruppenlink") && b.gruppenlink.target === "angebot") {
    offerSources.push("Gruppenlink");
    offerBase += Math.round(groupClicks * 0.25);
  }
  if (offerSources.length) {
    addResult("einloesungen", "Gutscheine eingelöst", `über ${offerSources.join(" und ")}`, offerBase);
  }

  const jobSources = [];
  let jobBase = 0;
  if (qr && setup.qrTarget === "karriere") {
    jobSources.push("QR-Codes");
    jobBase += 1;
  }
  if (on("gruppenlink") && b.gruppenlink.target === "karriere") {
    jobSources.push("Gruppenlink");
    jobBase += 2;
  }
  if (jobSources.length) {
    addResult("bewerbungen", "Bewerbungen", `über ${jobSources.join(" und ")}`, jobBase);
  }

  if (on("vorteil")) {
    addResult("vorteil", "Abschlüsse Spielervorteil", sponsor.offerLabel, 6, { group: CLUB.squad });
  }
  results.forEach((r) => {
    if (r.group) r.value = Math.min(r.value, r.group);
    r.hidden = Boolean(r.group) && r.value < MIN_GROUP;
    r.display = r.group ? `${r.value} von ${r.group}` : fmt(r.value);
  });
  const resultsLocked = perspective === "verein" && !setup.shareResults;

  // Fotos für die Kanäle des Unternehmens
  let fotos = null;
  if (on("fotos")) {
    const qty = b.fotos.qty;
    const due = Math.floor(qty * progress);
    const delivered = Math.min(qty, due + 1);
    fotos = {
      qty,
      due,
      delivered,
      downloaded: Math.max(0, delivered - 2),
      captions: PHOTO_CAPTIONS.slice(0, delivered),
    };
  }

  // Leistungsnachweis
  const club = [];
  const push = (item) => club.push({ proof: false, source: "angabe", total: 1, ...item });
  if (on("bande")) {
    push({
      id: "bande",
      icon: RectangleHorizontal,
      title: b.bande.qty > 1 ? `${b.bande.qty} Banden am Hauptplatz` : "Bande am Hauptplatz",
      detail: "hängt seit 03.08., Kontrollfoto zur Halbserie folgt",
      done: 1,
      due: 1,
      proof: true,
    });
  }
  if (on("banner")) {
    push({
      id: "banner",
      icon: Flag,
      title: b.banner.qty > 1 ? `${b.banner.qty} Banner` : "Banner im Vereinsheim",
      detail: "hängt seit 10.08.",
      done: 1,
      due: 1,
      proof: true,
    });
  }
  if (on("trikot")) {
    push({
      id: "trikot",
      icon: Shirt,
      title: "Logo auf dem Trikot",
      detail: `${CLUB.team}, bisher ${played.length * 2} Spiele mit Trikot`,
      done: 1,
      due: 1,
      proof: true,
    });
  }
  if (on("training")) {
    push({
      id: "training",
      icon: Dumbbell,
      title: "Trainingskleidung und -utensilien",
      detail: `${b.training.qty} Mannschaften ausgestattet`,
      done: 1,
      due: 1,
      proof: true,
    });
  }
  if (on("durchsage")) {
    push({
      id: "durchsage",
      icon: Megaphone,
      title: "Stadiondurchsage",
      detail: "bei jedem Heimspiel, abgehakt am Spieltag",
      done: played.filter((g) => g.done?.durchsage).length,
      due: played.length,
      total: SEASON.homeGames,
    });
  }
  if (on("heft")) {
    const done = played.filter((g) => g.done?.heft).length;
    push({
      id: "heft",
      icon: Newspaper,
      title: "Anzeige in Heft und Flyer",
      detail: `${fmt(done * 200)} Hefte verteilt${b.heft.qr ? ", mit QR-Code" : ""}`,
      done,
      due: played.length,
      total: SEASON.homeGames,
      proof: true,
    });
  }
  if (social) {
    push({
      id: "social",
      icon: Share2,
      title: "Social-Media-Beiträge",
      detail: "automatisch erkannt über die Schnittstelle",
      done: social.done,
      due: social.due,
      total: social.qty,
      source: "gemessen",
    });
  }
  if (on("website")) {
    push({
      id: "website",
      icon: Globe,
      title: "Nennung auf der Vereinswebsite",
      detail: "vorhanden und verlinkt, zuletzt heute geprüft",
      done: 1,
      due: 1,
      source: "gemessen",
    });
  }
  if (fotos) {
    push({
      id: "fotos",
      icon: Camera,
      title: "Fotos für eure Kanäle",
      detail: `${fotos.downloaded} davon heruntergeladen`,
      done: fotos.delivered,
      due: fotos.due,
      total: fotos.qty,
      source: "gemessen",
    });
  }
  if (on("vorteil")) {
    push({
      id: "vorteil",
      icon: Gift,
      title: sponsor.offerLabel,
      detail: `in der Mannschaftsgruppe der ${CLUB.team} geteilt`,
      done: 1,
      due: 1,
    });
  }
  if (on("gruppenlink")) {
    push({
      id: "gruppenlink",
      icon: Link2,
      title: "Link in Vereinsgruppen",
      detail: `${TARGETS[b.gruppenlink.target]}, ${shares} Mal geteilt`,
      done: 1,
      due: 1,
    });
  }
  if (on("treff")) {
    push({
      id: "treff",
      icon: Handshake,
      title: "Sponsorentreff",
      detail:
        b.treff.qty > 1
          ? "am 15.10. im Vereinsheim, der nächste folgt im Frühjahr"
          : "am 15.10. im Vereinsheim",
      done: 1,
      due: 1,
      total: b.treff.qty,
      proof: true,
    });
  }
  if (on("sonstiges")) {
    push({
      id: "sonstiges",
      icon: Package,
      title: b.sonstiges.label?.trim() || sponsor.otherLabel,
      detail: "eigene Leistung aus dem Setup, per Foto belegt",
      done: 1,
      due: 1,
      proof: true,
    });
  }

  const sponsorSide = [
    {
      id: "geld",
      icon: Wallet,
      title: `${euro(setup.volume)} Sponsoring`,
      detail: "erste Hälfte erhalten, zweite fällig im Februar",
      done: 1,
      due: 1,
      total: 2,
      source: "angabe",
    },
  ];
  if (setup.inKind) {
    sponsorSide.push({
      id: "sach",
      icon: Gift,
      title: sponsor.inKindLabel,
      detail: "übergeben, vom Verein bestätigt",
      done: 1,
      due: 1,
      total: 1,
      source: "angabe",
      proof: true,
    });
  }

  const dueSum = sum(club.map((i) => i.due));
  const doneSum = sum(club.map((i) => Math.min(i.done, i.due)));
  const fulfilment = {
    due: dueSum,
    done: doneSum,
    pct: dueSum ? Math.round((doneSum / dueSum) * 100) : 100,
  };

  // Aufgaben des Vereins
  const tasks = [];
  if (nextGame) {
    tasks.push({
      id: "game",
      icon: CalendarDays,
      title: `Heimspiel ${nextGame.n} eintragen`,
      detail: `${nextGame.weekday} ${nextGame.date}, gegen ${nextGame.opponent}`,
      action: true,
    });
  }
  if (on("bande")) {
    tasks.push({ id: "bande", icon: Camera, title: "Kontrollfoto der Bande", detail: "zur Halbserie, bis 13.12." });
  }
  if (social && social.done < social.qty) {
    tasks.push({
      id: "social",
      icon: Share2,
      title: `Beitrag ${social.done + 1} von ${social.qty} für ${sponsor.name}`,
      detail: "wird automatisch erkannt, sobald er online ist",
    });
  }
  if (fotos && fotos.delivered < fotos.qty) {
    tasks.push({
      id: "fotos",
      icon: Camera,
      title: "Fotos hochladen",
      detail: `${fotos.delivered} von ${fotos.qty} geliefert`,
    });
  }
  if (on("treff") && b.treff.qty > 1) {
    tasks.push({ id: "treff", icon: Handshake, title: "Nächsten Sponsorentreff planen", detail: "Termin im Frühjahr festlegen" });
  }

  // Die vier Kennzahlen oben, sortiert nach dem Schwerpunkt
  const kpis = {
    erfuellung: {
      label: "Leistungen erbracht",
      value: `${fulfilment.pct} %`,
      sub: `${fulfilment.done} von ${fulfilment.due} bisher fälligen`,
      source: "angabe",
      by: "Verein",
      proof: true,
    },
  };
  if (spectators) {
    kpis.zuschauer = {
      label: "Zuschauer",
      value: fmt(spectators.total),
      sub: spectators.youth ? "alle Mannschaften" : `${played.length} Heimspiele, Ø ${spectators.avg}`,
      source: "angabe",
      by: "Verein",
    };
  }
  if (contacts) {
    kpis.kontakte = {
      label: "Kontakte vor Ort",
      value: `≈ ${fmt(contacts.total)}`,
      sub: "hochgerechnet, Rechenweg unten",
      source: "schaetzung",
    };
  }
  if (social && social.done) {
    kpis.social = {
      label: "Aufrufe Partnerbeiträge",
      value: fmt(social.views),
      sub: `${social.done} Beiträge auf Instagram`,
      source: "gemessen",
    };
  }
  if (qr) {
    kpis.qr = {
      label: "QR-Scans",
      value: fmt(qr.total),
      sub: `${qr.placements.length} ${qr.placements.length === 1 ? "Platzierung" : "Platzierungen"}`,
      source: "gemessen",
    };
  }
  if (channels.length) {
    kpis.klicks = {
      label: "Link-Klicks",
      value: fmt(clicks),
      sub: `${channels.length} ${channels.length === 1 ? "Kanal" : "Kanäle"}`,
      source: "gemessen",
    };
  }
  const shownResult = results.find((r) => !r.hidden);
  if (shownResult && !resultsLocked) {
    kpis.ergebnis = {
      label: shownResult.label,
      value: shownResult.display,
      sub: `${shownResult.detail}, gemeldet von ${sponsor.name}`,
      source: "angabe",
      by: "Unternehmen",
    };
  }
  if (fotos) {
    kpis.fotos = {
      label: "Fotos geliefert",
      value: `${fotos.delivered} von ${fotos.qty}`,
      sub: `${fotos.downloaded} heruntergeladen`,
      source: "gemessen",
    };
  }
  const order = [...new Set([...FOCUS_ORDER[setup.focus], ...Object.keys(kpis)])];
  const highlights = order
    .filter((k) => kpis[k])
    .slice(0, 4)
    .map((k) => ({ key: k, ...kpis[k] }));

  return {
    sponsor,
    setup,
    games,
    played,
    passed: passed.length,
    progress,
    nextGame,
    spectators,
    contacts,
    qr,
    social,
    channels,
    clicks,
    results,
    resultsLocked,
    fotos,
    deliveries: { club, sponsor: sponsorSide },
    fulfilment,
    tasks,
    highlights,
    activeBlocks: BLOCKS.filter((block) => on(block.id)),
    perspective,
  };
}
